import React, { useState, useMemo } from 'react';
import { MasterFlightRecord, formatFrequencyWithUnderscores, getDisplayFrequency, getDisplayDate, getRouteString, formatDisplayDate } from '../../../utils/types';
import { useTimezone } from '../../../contexts/TimezoneContext';
import { SectionHeader } from '../common/SectionHeader';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { ItineraryFullscreenModal } from './ItineraryFullscreenModal';

interface VariantsTableProps {
  variants: MasterFlightRecord[];
  currentVariantId: string; // _id of currently displayed variant
  flightOwner: string;
  onVariantSelect: (variant: MasterFlightRecord) => void;
  onCompareOpen: () => void;
}

const STATUS_OPTIONS = ['All', 'ACTIVE', 'INACTIVE', 'PENDING', 'CANCELLED', 'SUSPENDED'] as const;

/**
 * Airline-specific variant patterns
 * - Per-Season airlines: MH, AA, etc. - variants reset each season
 * - Global airlines: OD - variants continue across all seasons
 */
const GLOBAL_VARIANT_AIRLINES = ['OD'];

const thClass = 'px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider';

/**
 * VariantsTable Component
 *
 * Displays all variants of a flight number in a compact, scannable table.
 * For per-season airlines (MH, AA): Shows season filter buttons
 * For global airlines (OD): Shows all variants across all seasons
 */
export function VariantsTable({
  variants,
  currentVariantId,
  flightOwner,
  onVariantSelect,
  onCompareOpen,
}: VariantsTableProps) {
  const { timezone } = useTimezone();
  const isGlobalAirline = GLOBAL_VARIANT_AIRLINES.includes(flightOwner.toUpperCase());

  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

  // Get unique seasons from variants
  const availableSeasons = useMemo(() => {
    const seasons = Array.from(new Set(variants.map(v => v.seasonId)));
    return seasons.sort();
  }, [variants]);

  // Current season filter (default to "All")
  const [selectedSeason, setSelectedSeason] = useState<string>('All');
  const [statusFilter, setStatusFilter] = useState<string>('All');

  // Filter variants by season and status
  const filteredVariants = useMemo(() => {
    let result = variants;
    if (!isGlobalAirline && selectedSeason !== 'All') {
      result = result.filter(v => v.seasonId === selectedSeason);
    }
    if (statusFilter !== 'All') {
      result = result.filter(v => (v.scheduleStatus || '').toUpperCase() === statusFilter);
    }
    return result;
  }, [variants, selectedSeason, isGlobalAirline, statusFilter]);

  // Sort variants by itineraryVarId
  const sortedVariants = useMemo(() => {
    return [...filteredVariants].sort((a, b) => a.itineraryVarId - b.itineraryVarId);
  }, [filteredVariants]);

  // Season group dividers — detect group boundaries
  const seasonGroupInfo = useMemo(() => {
    const map = new Map<number, { seasonId: string; count: number }>();
    let currentSeason: string | null = null;

    sortedVariants.forEach((v, idx) => {
      if (v.seasonId !== currentSeason) {
        currentSeason = v.seasonId;
        // Count how many in this group
        let count = 0;
        for (let j = idx; j < sortedVariants.length && sortedVariants[j].seasonId === currentSeason; j++) {
          count++;
        }
        map.set(idx, { seasonId: v.seasonId, count });
      }
    });

    return map;
  }, [sortedVariants]);


  if (variants.length === 0) {
    return null;
  }

  const totalColumns = 13;

  return (
    <div className="mb-6">
      {/* Section Header with Season Filter + Status Filter + Compare Buttons */}
      <div className="flex items-center justify-between mb-3">
        <SectionHeader title={`Flight Itinerary (${filteredVariants.length})`} />

        <div className="flex items-center gap-3">
          {/* Fullscreen button */}
          <button
            onClick={() => setIsFullscreenOpen(true)}
            title="Expand to fullscreen"
            className="px-2 py-1 text-xs rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <OpenInFullIcon sx={{ fontSize: 14 }} />
          </button>

          {/* Compare button */}
          {variants.length >= 2 && (
            <button
              onClick={onCompareOpen}
              className="px-2.5 py-1 text-xs font-semibold rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Compare
            </button>
          )}

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-2.5 py-1 text-xs font-semibold rounded border border-gray-300 bg-white text-gray-700 cursor-pointer"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt === 'All' ? 'All Statuses' : opt}</option>
            ))}
          </select>

          {/* Season Filter Buttons (only for per-season airlines) */}
          {!isGlobalAirline && availableSeasons.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 font-medium">Season:</span>
              <div className="flex gap-1">
                {availableSeasons.map(season => (
                  <button
                    key={season}
                    onClick={() => setSelectedSeason(season)}
                    className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${
                      selectedSeason === season
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {season}
                  </button>
                ))}
                <button
                  onClick={() => setSelectedSeason('All')}
                  className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${
                    selectedSeason === 'All'
                      ? 'bg-primary-500 text-white'
                      : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Itinerary Table — Scrollable */}
      <div
        className="bg-white rounded-lg border border-gray-200 max-h-[280px] overflow-y-auto"
      >
        <table className="w-full text-md table-fixed">
          <thead className="bg-gray-100 border-b border-gray-200 sticky top-0 z-10">
            <tr>
              <th className={thClass}>
                {/* Active indicator column */}
              </th>
              <th className={thClass}>Season</th>
              <th className={thClass}>Service Type</th>
              <th className={thClass}>Status</th>
              <th className={thClass}>Start Date</th>
              <th className={thClass}>End Date</th>
              <th className={thClass}>Frequency</th>
              <th className={thClass}>Op. Suffix</th>
              <th className={thClass}>Route</th>
              <th className={thClass}>Sectors</th>
              <th className={thClass}>Aircraft</th>
              <th className={thClass}>Config</th>
              <th className={thClass}>Onward Flight</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedVariants.map((variant, idx) => {
              const isCurrentVariant = variant._id === currentVariantId;
              const serviceTypeDisplay = variant.iataServiceTypeCode || '—';

              // Get aircraft type from first station (most common across sectors)
              const aircraftType = variant.stations?.[0]?.iataSubTypeCode || '—';
              const aircraftConfig = variant.stations?.[0]?.aircraftConfiguration || '—';

              // Season group divider
              const groupInfo = seasonGroupInfo.get(idx);

              return (
                <React.Fragment key={variant._id}>
                  {/* Season group divider */}
                  {groupInfo && seasonGroupInfo.size > 1 && (
                    <tr>
                      <td
                        colSpan={totalColumns}
                        className="px-4 py-1.5 text-xs text-gray-400 font-medium tracking-wider border-t border-dashed border-gray-300 bg-gray-50"
                      >
                        <span className="inline-flex items-center gap-2">
                          <span className="h-px w-4 bg-gray-300" />
                          {groupInfo.seasonId} ({groupInfo.count} variant{groupInfo.count !== 1 ? 's' : ''})
                          <span className="h-px w-4 bg-gray-300" />
                        </span>
                      </td>
                    </tr>
                  )}

                  <tr

                    onClick={() => onVariantSelect(variant)}
                    className={`cursor-pointer transition-colors ${
                      isCurrentVariant
                        ? 'bg-blue-50 hover:bg-blue-100'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* Active indicator */}
                    <td className="px-2 py-2 text-center">
                      <span className={`text-lg ${isCurrentVariant ? 'text-blue-600' : 'text-gray-400'}`}>
                        {isCurrentVariant ? '●' : '○'}
                      </span>
                    </td>

                    {/* Season */}
                    <td className="px-2 py-2 text-center">
                      <span className={`text-md ${isCurrentVariant ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {variant.seasonId}
                        {variant.isAdhoc && (
                          <span className="ml-1 px-1 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold uppercase leading-none">
                            Adhoc
                          </span>
                        )}
                      </span>
                    </td>

                    {/* Service Type */}
                    <td className="px-2 py-2 text-center">
                      <span className={`text-md ${isCurrentVariant ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                        {serviceTypeDisplay}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-2 py-2 text-center">
                      <span className={`text-md ${isCurrentVariant ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                        {variant.scheduleStatus || '—'}
                      </span>
                    </td>

                    {/* Start Date */}
                    <td className="px-2 py-2 text-center">
                      <span className={`text-md ${isCurrentVariant ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                        {formatDisplayDate(getDisplayDate(variant.startDate, variant.stations?.[0]?.std, timezone))}
                      </span>
                    </td>

                    {/* End Date */}
                    <td className="px-2 py-2 text-center">
                      <span className={`text-md ${isCurrentVariant ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                        {formatDisplayDate(getDisplayDate(variant.endDate, variant.stations?.[0]?.std, timezone))}
                      </span>
                    </td>

                    {/* Frequency */}
                    <td className="px-2 py-2 text-center">
                      <span className={`font-mono text-md tracking-wide ${
                        isCurrentVariant ? 'font-semibold text-gray-900' : 'text-gray-600'
                      }`}>
                        {formatFrequencyWithUnderscores(getDisplayFrequency(variant.frequency, variant.frequencyMYT, timezone))}
                      </span>
                    </td>

                    {/* Op. Suffix */}
                    <td className="px-2 py-2 text-center">
                      <span className={`font-mono text-md ${isCurrentVariant ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                        {variant.operationalSuffix || '—'}
                      </span>
                    </td>

                    {/* Route */}
                    <td className="px-2 py-2 text-center">
                      <span className={`text-md ${isCurrentVariant ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                        {getRouteString(variant.stations)}
                      </span>
                    </td>

                    {/* Sectors Count */}
                    <td className="px-2 py-2 text-center">
                      <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold ${
                        isCurrentVariant
                          ? 'bg-blue-200 text-blue-800'
                          : 'bg-gray-200 text-gray-700'
                      }`}>
                        {variant.stations?.length || 0}
                      </span>
                    </td>

                    {/* Aircraft */}
                    <td className="px-2 py-2 text-center">
                      <span className={`text-md ${isCurrentVariant ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                        {aircraftType}
                      </span>
                    </td>

                    {/* Config */}
                    <td className="px-2 py-2 text-center">
                      <span className={`font-mono text-md ${isCurrentVariant ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                        {aircraftConfig}
                      </span>
                    </td>

                    {/* Onward Flight */}
                    <td className="px-2 py-2 text-center">
                      <span className={`font-mono text-md ${isCurrentVariant ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                        {variant.stations?.[0]?.onwardFlight || '—'}
                      </span>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Helper text */}
      <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
        Click any row to view that variant's details above
      </p>

      <ItineraryFullscreenModal
        open={isFullscreenOpen}
        variants={variants}
        currentVariantId={currentVariantId}
        flightOwner={flightOwner}
        onVariantSelect={(variant) => {
          onVariantSelect(variant);
          setIsFullscreenOpen(false);
        }}
        onClose={() => setIsFullscreenOpen(false)}
      />
    </div>
  );
}
